import React, { useState, useEffect } from 'react';
import { Dialog, Box, Divider, DialogActions, DialogContent, DialogTitle, TextField, Button, Typography, Snackbar, Alert, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { setDoc, doc, getDocs, query, where, getDoc, collection } from 'firebase/firestore';
import { db } from '../../../../firebase'; // Ensure correct Firebase path
import { textFieldStyles } from '../../../../Login/LoginPageStyles';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import LeaveDeduction from './LeaveDeduction';

// Calculate total salary based on hourly wage and monthly hours
const calculateTotalSalary = (hourlyWage, monthlyHours) => {
  const [hoursPart, minutesPart] = monthlyHours.split(' hours ');
  const hours = parseInt(hoursPart, 10);
  const minutes = parseInt(minutesPart.replace(' minutes', ''), 10);
  const totalHours = hours + minutes / 60;
  return hourlyWage * totalHours;
};

const SalaryDialog = ({ open, onClose, user, onSave }) => {
  const [hourlyWage, setHourlyWage] = useState('');
  const [monthlyHours, setMonthlyHours] = useState('');
  const [totalSalary, setTotalSalary] = useState(0);
  const [allowances, setAllowances] = useState([]);
  const [allowanceDescription, setAllowanceDescription] = useState('');
  const [allowancePercentage, setAllowancePercentage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [monthOptions, setMonthOptions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [deductions, setDeductions] = useState([]);
  const [deductionDescription, setDeductionDescription] = useState('');
  const [deductionPercentage, setDeductionPercentage] = useState('');
  const [salaryDeduction, setSalaryDeduction] = useState(0);

    // This function will be passed to LeaveDeduction to receive the salary deduction
    const handleSalaryDeduction = (deduction) => {
      setSalaryDeduction(deduction);
    };

  useEffect(() => {
    if (open) {
      const fetchSalaryData = async () => {
        try {
          // Fetch hourly wage
          const employeeRef = doc(db, 'employees', user.id);
          const employeeDoc = await getDoc(employeeRef);

          if (employeeDoc.exists()) {
            const employeeData = employeeDoc.data();
            setHourlyWage(employeeData.hourlyWage);

            // Query for monthlyHours documents
            const monthlyHoursQuery = query(
              collection(db, 'employeeMonthlyHours'),
              where('employeeId', '==', user.id)
            );
            const querySnapshot = await getDocs(monthlyHoursQuery);

            const monthDocs = [];
            querySnapshot.forEach((doc) => {
              const data = doc.data();
              monthDocs.push({ month: data.month, monthlyHours: data.monthlyHours });
            });

            setMonthOptions(monthDocs);
            if (monthDocs.length > 0) {
              setSelectedMonth(monthDocs[0].month); // Default to the first month
              setMonthlyHours(monthDocs[0].monthlyHours); // Default to the first month's hours
            }
          } else {
            console.error("Employee document does not exist.");
          }
        } catch (error) {
          console.error("Error fetching salary details: ", error);
        }
      };

      fetchSalaryData();
    }
  }, [open, user.id]);

  useEffect(() => {
    if (hourlyWage && monthlyHours) {
      const calculatedSalary = calculateTotalSalary(parseFloat(hourlyWage), monthlyHours);
      setTotalSalary(calculatedSalary);
    }
  }, [hourlyWage, monthlyHours]);

  useEffect(() => {
    if (selectedMonth) {
      const fetchExistingSalaryData = async () => {
        try {
          const salaryRef = doc(db, 'employeeSalary', `${user.id}-${selectedMonth}`);
          const salaryDoc = await getDoc(salaryRef);

          if (salaryDoc.exists()) {
            const salaryData = salaryDoc.data();
            setAllowances(salaryData.allowances || []);
            setDeductions(salaryData.deductions || []); // Update local allowances
          } else {
            setAllowances([]);
            setDeductions([]);
          }
        } catch (error) {
          console.error("Error fetching existing salary details: ", error);
        }
      };

      fetchExistingSalaryData();
    }
  }, [selectedMonth, user.id]);

  const handleAddAllowance = () => {
    if (allowanceDescription && allowancePercentage) {
      const calculatedAllowance = (parseFloat(allowancePercentage) / 100) * totalSalary;

      // Check if allowance with the same description already exists
      const existingAllowanceIndex = allowances.findIndex(allowance => allowance.description === allowanceDescription);

      if (existingAllowanceIndex > -1) {
        // Update existing allowance
        const updatedAllowances = allowances.map((allowance, index) =>
          index === existingAllowanceIndex
            ? { ...allowance, percentage: parseFloat(allowancePercentage), value: calculatedAllowance }
            : allowance
        );
        setAllowances(updatedAllowances);
      } else {
        // Add new allowance
        const newAllowance = {
          description: allowanceDescription,
          percentage: parseFloat(allowancePercentage),
          value: calculatedAllowance
        };
        setAllowances(prev => [...prev, newAllowance]); // Add new allowance
      }

      // Clear input fields
      setAllowanceDescription('');
      setAllowancePercentage('');
    }
  };

  const handleMonthChange = async (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth);

    const selectedMonthDoc = monthOptions.find(option => option.month === newMonth);
    if (selectedMonthDoc) {
      setMonthlyHours(selectedMonthDoc.monthlyHours);
    }

    setAllowances([]); // Reset allowances when month changes

    const fetchExistingAllowances = async () => {
      try {
        const salaryRef = doc(db, 'employeeSalary', `${user.id}-${newMonth}`);
        const salaryDoc = await getDoc(salaryRef);

        if (salaryDoc.exists()) {
          const salaryData = salaryDoc.data();
          setAllowances(salaryData.allowances || []); // Update local allowances
        } else {
          setAllowances([]);
        }
      } catch (error) {
        console.error("Error fetching existing allowances: ", error);
      }
    };

    fetchExistingAllowances();
  };

  const handleAddDeduction = () => {
    if (deductionDescription && deductionPercentage) {
      const calculatedDeduction = (parseFloat(deductionPercentage) / 100) * totalSalary;
  
      // Check if deduction with the same description already exists
      const existingDeductionIndex = deductions.findIndex(deduction => deduction.description === deductionDescription);
  
      if (existingDeductionIndex > -1) {
        // Update existing deduction
        const updatedDeductions = deductions.map((deduction, index) =>
          index === existingDeductionIndex
            ? { ...deduction, percentage: parseFloat(deductionPercentage), value: calculatedDeduction }
            : deduction
        );
        setDeductions(updatedDeductions);
      } else {
        // Add new deduction
        const newDeduction = {
          description: deductionDescription,
          percentage: parseFloat(deductionPercentage),
          value: calculatedDeduction
        };
        setDeductions(prev => [...prev, newDeduction]); // Add new deduction
      }
  
      // Clear input fields
      setDeductionDescription('');
      setDeductionPercentage('');
    }
  };
  

  const totalDeductions = deductions.reduce((acc, curr) => acc + curr.value, 0);
  const finalSalary = totalSalary - (totalDeductions + salaryDeduction);

  const handleSubmit = async () => {
    try {
      const salaryRef = doc(db, 'employeeSalary', `${user.id}-${selectedMonth}`);
      await setDoc(salaryRef, {
        userId: user.id,
        name: user.name,
        position: user.position,
        month: selectedMonth,
        grossPay:totalSalary,
        netPay: finalSalary,
        leaveDeduction: salaryDeduction,
        allowances,
        deductions,
        monthlyHours,
        timestamp: new Date(),
      });
  
      onSave({
        userId: user.id,
        totalSalary,
        allowances,
        deductions,
      });
  
      // Clear input fields after saving
      setAllowanceDescription('');
      setAllowancePercentage('');
      setSnackbarOpen(true);
      onClose();
    } catch (error) {
      console.error("Error saving salary details: ", error);
    }
  };
  
  const handleDeleteAllowance = (index) => {
    setAllowances(prevAllowances => prevAllowances.filter((_, i) => i !== index));
  };

  const handleDeleteDeduction = (index) => {
    setDeductions(prevDeductions => prevDeductions.filter((_, i) => i !== index));
  };
  

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Assign Salary to {user.name}</DialogTitle>
        <DialogContent>

          <Typography variant="h6" gutterBottom>
            <AttachMoneyIcon fontSize="small" /> Salary Calculation
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ padding: 2 }}>
            <FormControl fullWidth sx={{ ...textFieldStyles, mb: 2 }} variant="outlined">
              <InputLabel shrink>Select Month</InputLabel>
              <Select
                value={selectedMonth}
                onChange={handleMonthChange}
                label="Select Month" // Assign the label here for proper association
              >
                {monthOptions.map((option, index) => (
                  <MenuItem key={index} value={option.month}>
                    {option.month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body1"><strong>Hourly Wage:</strong> PKR {hourlyWage}</Typography>
            <Typography variant="body1"><strong>Monthly Hours:</strong> {monthlyHours}</Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>
              <strong>Gross Pay:</strong> PKR {totalSalary.toFixed(2)}
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom>
            <PlaylistAddIcon fontSize="small" /> Add Earnings
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 1, padding: 2 }}>
          <TextField
              label="Earning Description"
              fullWidth
              variant="outlined"
              sx={{ ...textFieldStyles, flex: 2 }} // Give more space to the description
              value={allowanceDescription}
              onChange={(e) => setAllowanceDescription(e.target.value)}
            />
            <TextField
              label="Percentage (%)"
              type="number"
              value={allowancePercentage}
              onChange={(e) => setAllowancePercentage(e.target.value)}
              fullWidth
              sx={{ ...textFieldStyles, flex:1}}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddAllowance}
              sx={{ alignSelf: 'center', backgroundColor: '#39B54A', '&:hover': { backgroundColor: '#2c8f39' } }}
            >
              Add
            </Button>
          </Box>

          <Box sx={{ padding: 2, mb: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><b>Description</b></TableCell>
                  <TableCell><b>Percentage</b></TableCell>
                  <TableCell><b>Value</b></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allowances.map((allowance, index) => (
                  <TableRow key={index}>
                    <TableCell>{allowance.description}</TableCell>
                    <TableCell>{allowance.percentage.toFixed(2)}%</TableCell>
                    <TableCell>{allowance.value.toFixed(2)}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteAllowance(index)} // Call a function to delete the allowance
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

            </Table>
          </TableContainer>
          </Box>

          <Typography variant="h6" gutterBottom>
            <PlaylistRemoveIcon fontSize="small" /> Add Deductions
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 1, padding: 2 }}>
            <TextField
              label="Deduction Description"
              variant="outlined"
              fullWidth
              value={deductionDescription}
              onChange={(e) => setDeductionDescription(e.target.value)}
              sx={{ ...textFieldStyles, flex:2 }}
            />
            <TextField
              label="Percentage (%)"
              variant="outlined"
              fullWidth
              value={deductionPercentage}
              onChange={(e) => setDeductionPercentage(e.target.value)}
              sx={{ ...textFieldStyles, flex: 1 }}
            />
            <Button
              onClick={handleAddDeduction}
              variant="contained"
              color="primary"
              sx={{ alignSelf: 'center', backgroundColor: '#39B54A', '&:hover': { backgroundColor: '#2c8f39' } }}
            >
              Add
            </Button>
          </Box>

          <Box sx={{ padding: 2 }}>
          <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                  <TableCell><b>Description</b></TableCell>
                  <TableCell><b>Percentage</b></TableCell>
                  <TableCell><b>Value</b></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deductions.map((deduction, index) => (
                    <TableRow key={index}>
                      <TableCell>{deduction.description}</TableCell>
                      <TableCell>{deduction.percentage}%</TableCell>
                      <TableCell>{deduction.value.toFixed(2)}</TableCell>
                      <TableCell>
                        <Button
                          onClick={() => handleDeleteDeduction(index)}
                          variant="contained"
                          color="error"
                          size="small"
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Divider sx={{ mb: 2, mt:2 }} />
          <LeaveDeduction
            user={user}
            selectedMonth={selectedMonth}
            onSalaryDeduction={handleSalaryDeduction}  // Pass the callback function
          />
          <Divider sx={{ mb: 2, mt:2 }} />
          <Typography variant="h6"><strong>Net Pay:</strong> PKR {finalSalary}</Typography>

        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ color: 'red' }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained"
           sx={{ backgroundColor: '#39B54A', '&:hover': { backgroundColor: '#2c8f39' } }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="success">
          Salary details saved successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default SalaryDialog;
